import { useConfigContext } from '@/context/configContext';
import {
    BudgetLimits,
    useBudgetLimits,
} from '@/dashboards/CampaignPlanner/hooks/useBudgetLimits';
import { useCampaignPlannerContext } from '@/dashboards/CampaignPlanner/hooks/useCampaignPlannerContext';
import { useCampaignPlannerUrl } from '@/dashboards/CampaignPlanner/hooks/useCampaignPlannerUrl';
import { OptimisationBase as OptimisationBaseType } from '@/optimizer';
import { useOptimizer } from '@/optimizer/hooks/useOptimizer';
import { DateFormat, formatDate } from '@/utils/dateUtils';
import { Button, Modal, ProgressStepper } from '@analytical-alley/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useCampaignFormDefaults } from './hooks/useCampaignFormDefaults';
import {
    CampaignFormData,
    makeSchema,
    STEP_LABELS,
    stepFieldsMap,
    TOTAL_STEPS,
} from './schema';
import { BudgetStep } from './steps/BudgetStep';
import { ChannelsStep } from './steps/ChannelsStep';
import { DatesStep } from './steps/DatesStep';
import { NameStep } from './steps/NameStep';
import { TypeStep } from './steps/TypeStep';

export const CreateCampaignModal = () => {
    const {
        showSaveCampaignDialog,
        setShowSaveCampaignDialog,
        saveCampaign,
        isSavingCampaign,
        saveAsCampaignReference,
        setSaveAsCampaignReference,
        medias,
    } = useCampaignPlannerContext();

    const {
        params: { projectId },
    } = useCampaignPlannerUrl();

    const { rules } = useOptimizer();
    const { locale, currency } = useConfigContext();

    const { defaultStartDate, defaultEndDate, dateConstraints } =
        useCampaignFormDefaults();

    const [currentStep, setCurrentStep] = useState(0);
    const [disabledChannels, setDisabledChannels] = useState<string[]>([]);

    const mediasWithDisabled = medias.map((media) => ({
        ...media,
        selected: !saveAsCampaignReference?.disabledChannels?.includes(
            media.id!,
        ),
    }));

    // A ref that always holds the latest budgetLimits so the resolver closure
    // sees current limits on every validation call without needing watch values
    // to be available before useForm is called.
    const budgetLimitsRef = useRef<BudgetLimits>({ min: 0, max: 0 });

    const {
        handleSubmit,
        formState: { errors, isValid },
        watch,
        setValue,
        trigger,
        reset,
    } = useForm<CampaignFormData>({
        // RHF stores the resolver in its own internal ref and updates it every
        // render, so the fresh makeSchema(budgetLimitsRef.current) is always
        // used when validation is triggered.
        resolver: (values, context, options) =>
            zodResolver(makeSchema(budgetLimitsRef.current))(
                values,
                context,
                options,
            ),
        defaultValues: {
            name: '',
            budget: 0,
            startDate: defaultStartDate,
            endDate: defaultEndDate,
            optimizationBase: 'budget',
        },
    });

    const startDate = watch('startDate');
    const endDate = watch('endDate');
    const budget = watch('budget');
    const optimizationBase = watch('optimizationBase');
    const shouldLookupLimits = showSaveCampaignDialog && currentStep === 4;
    const limitsRefreshToken = `${showSaveCampaignDialog}-${currentStep}-${optimizationBase}-${startDate.getTime()}-${endDate.getTime()}`;

    const { limits: budgetLimits, loading: budgetLimitsLoading } =
        useBudgetLimits({
            disabledChannels,
            startDate,
            endDate,
            rules,
            optimizationBase,
            enabled: shouldLookupLimits,
            refreshToken: limitsRefreshToken,
        });

    // Keep the ref current so the resolver always reads the latest limits.
    budgetLimitsRef.current = budgetLimits;

    useEffect(() => {
        if (!showSaveCampaignDialog) {
            return;
        }

        if (saveAsCampaignReference) {
            reset({
                name: `${saveAsCampaignReference.name} (Copy)`,
                budget: saveAsCampaignReference.budget,
                startDate: new Date(saveAsCampaignReference.startDate),
                endDate: new Date(saveAsCampaignReference.endDate),
                optimizationBase:
                    saveAsCampaignReference.optimizationBase ?? 'budget',
            });
            setDisabledChannels(saveAsCampaignReference.disabledChannels ?? []);
        } else {
            reset({
                name: '',
                budget: 0,
                startDate: defaultStartDate,
                endDate: defaultEndDate,
                optimizationBase: 'budget',
            });
            setDisabledChannels([]);
        }

        setCurrentStep(0);
    }, [
        showSaveCampaignDialog,
        saveAsCampaignReference,
        defaultStartDate,
        defaultEndDate,
        reset,
    ]);

    useEffect(() => {
        if (budget > 0) {
            trigger('budget');
        }
    }, [budgetLimits, trigger, budget]);

    const onClose = () => {
        setShowSaveCampaignDialog(false);
        setSaveAsCampaignReference(null);
    };

    const onSubmit = async (data: CampaignFormData) => {
        await saveCampaign({
            projectId,
            name: data.name,
            startDate: formatDate(data.startDate, DateFormat.URL),
            endDate: formatDate(data.endDate, DateFormat.URL),
            budget: data.budget,
            optimizationBase: data.optimizationBase,
            disabledChannels,
        });
    };

    const handleNext = async () => {
        const fields = stepFieldsMap[currentStep] ?? [];
        const valid = fields.length === 0 ? true : await trigger(fields as any);
        if (valid) {
            setCurrentStep((s) => s + 1);
        }
    };

    const handleBack = () => {
        setCurrentStep((s) => s - 1);
    };

    const steps = STEP_LABELS.map((label, index) => ({
        title: label,
        active: index === currentStep,
        completed: index < currentStep,
    }));

    const isLastStep = currentStep === TOTAL_STEPS - 1;
    const isFirstStep = currentStep === 0;

    return (
        <Modal
            dismissible
            show={showSaveCampaignDialog}
            onClose={onClose}
            size="3xl"
        >
            <Modal.Header>Create Campaign</Modal.Header>
            <Modal.Body className="text-white overflow-y-auto">
                <div className="mb-8">
                    <ProgressStepper steps={steps} />
                </div>

                <form
                    onSubmit={(e) => e.preventDefault()}
                    className="flex flex-col gap-4 min-h-[220px]"
                >
                    {currentStep === 0 && (
                        <NameStep
                            value={watch('name')}
                            onChange={(value) =>
                                setValue('name', value, {
                                    shouldValidate: true,
                                    shouldDirty: true,
                                })
                            }
                            errors={errors}
                        />
                    )}

                    {currentStep === 1 && (
                        <TypeStep
                            optimizationBase={optimizationBase}
                            onChange={(value) =>
                                setValue(
                                    'optimizationBase',
                                    value as OptimisationBaseType,
                                    {
                                        shouldValidate: true,
                                        shouldDirty: true,
                                    },
                                )
                            }
                        />
                    )}

                    {currentStep === 2 && (
                        <ChannelsStep
                            medias={medias}
                            mediasWithDisabled={mediasWithDisabled}
                            disabledChannels={disabledChannels}
                            onCheckedChange={(checkedNodes) => {
                                const disabledMedias: string[] = medias
                                    .filter(
                                        (media) =>
                                            !checkedNodes.some(
                                                (node) =>
                                                    node.label === media.label,
                                            ),
                                    )
                                    .map((media) => media.id!);
                                setDisabledChannels(disabledMedias);
                            }}
                        />
                    )}

                    {currentStep === 3 && (
                        <DatesStep
                            startDate={startDate}
                            endDate={endDate}
                            dateConstraints={dateConstraints}
                            onDateChange={(start, end) => {
                                setValue('startDate', start, {
                                    shouldValidate: true,
                                    shouldDirty: true,
                                });
                                setValue('endDate', end, {
                                    shouldValidate: true,
                                    shouldDirty: true,
                                });
                            }}
                            errors={errors}
                        />
                    )}

                    {currentStep === 4 && (
                        <BudgetStep
                            key={`${optimizationBase}-${budgetLimits.min}-${budgetLimits.max}-${budgetLimitsLoading}`}
                            budget={budget}
                            optimizationBase={optimizationBase}
                            budgetLimits={budgetLimits}
                            loading={budgetLimitsLoading}
                            locale={locale}
                            currency={currency}
                            onChange={(value) =>
                                setValue('budget', value, {
                                    shouldValidate: true,
                                    shouldDirty: true,
                                })
                            }
                        />
                    )}
                </form>
            </Modal.Body>
            <Modal.Footer className="py-4">
                {isFirstStep ? (
                    <Button
                        className="ml-2"
                        size="sm"
                        color="red"
                        onClick={onClose}
                    >
                        Cancel
                    </Button>
                ) : (
                    <Button
                        className="ml-2"
                        size="sm"
                        color="gray"
                        onClick={handleBack}
                    >
                        Back
                    </Button>
                )}
                {isLastStep ? (
                    <Button
                        className="ml-2"
                        size="sm"
                        color="light"
                        disabled={!isValid || isSavingCampaign}
                        isProcessing={isSavingCampaign}
                        onClick={handleSubmit(onSubmit)}
                    >
                        {isSavingCampaign ? 'Saving...' : 'Save'}
                    </Button>
                ) : (
                    <Button
                        className="ml-2"
                        size="sm"
                        color="light"
                        onClick={handleNext}
                    >
                        Next
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    );
};
